import { getCookie } from 'cookies-next/client';
import { fetchRemoteConfig } from '@/hotdesk-ui/common-classes/firebase.service';
import { RemoteConfigKeys } from '@/hotdesk-ui/enum/remote-keys';

export type GDPRConfigType = {
    enableTcf: boolean;
    perPurposeConsent: boolean;
    banner: Record<string, string>;
};

export type ConcentType = {
    consent: boolean;
    id: number;
    timestamp: string | Date;
    version: string;
};
export type APIError = {
    [key: string]: string | number | object;
    detail: string;
};
export declare class ApiException<T> extends Error {
    code: number;
    body: T;
    constructor(code: number, body: T);
}
export class GdprService {
    isGDPREnabled = false;
    gdprConfigURL!: string;
    gdprConfig!: GDPRConfigType;
    defaultCookiesPolicy!: number;
    cookiesPolicy!: { [key: string]: number };
    siteId!: number;
    gdprStyleUrl!: string;
    language!: string;
    apiKey!: string;
    consentLink!: string;

    async configureGDPR(): Promise<void> {
        this.isGDPREnabled = (await fetchRemoteConfig(
            RemoteConfigKeys.GDPR_ENABLED_KEY,
            'boolean'
        )) as boolean;
        if (process.env.STAGE !== 'prod') {
            this.isGDPREnabled = true;
        }

        if (this.isGDPREnabled) {
            this.gdprConfigURL = (await fetchRemoteConfig(
                RemoteConfigKeys.IUBENDA_CONFIG_JSON_URL_KEY,
                'string'
            )) as string;
            this.language = (await fetchRemoteConfig(
                RemoteConfigKeys.GDPR_LANGUAGE_KEY,
                'string'
            )) as string;
            this.gdprConfig = (await this.getConfigData(
                this.gdprConfigURL
            )) as GDPRConfigType;

            this.apiKey = (await fetchRemoteConfig(
                RemoteConfigKeys.IUBENDA_PUBLIC_API_KEY,
                'string'
            )) as string;

            this.consentLink = (await fetchRemoteConfig(
                RemoteConfigKeys.IUBENDA_CONSENT_LINK_KEY,
                'string'
            )) as string;

            try {
                this.cookiesPolicy = (await fetchRemoteConfig(
                    RemoteConfigKeys.IUBENDA_COOKIE_POLICY_ID_KEY,
                    'object'
                )) as { [key: string]: number };
            } catch {
                this.defaultCookiesPolicy = (await fetchRemoteConfig(
                    RemoteConfigKeys.IUBENDA_COOKIE_POLICY_ID_KEY,
                    'number'
                )) as number;
            }

            this.siteId = (await fetchRemoteConfig(
                RemoteConfigKeys.IUBENDA_SITE_ID_KEY,
                'number'
            )) as number;

            this.gdprStyleUrl = (await fetchRemoteConfig(
                RemoteConfigKeys.IUBENDA_CUSTOM_STYLE_CSS_URL_KEY,
                'string'
            )) as string;
            this.addConcentInstruction();
            this.addInstructionLib();
            this.loadGDPRConfig();
            this.addLib();
        }
    }
    addConcentInstruction() {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.id = 'cons_instructions';
        script.async = true;
        script.text = `var _iub = _iub || {}; 
        _iub.cons_instructions = _iub.cons_instructions || []; 
        _iub.cons_instructions.push(["init", {api_key: "${this.apiKey}"}]);`;
        document.head.appendChild(script);
    }

    loadGDPRConfig() {
        this.language = getCookie('langulage_code') || 'en';
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.id = 'csConfiguration';
        script.async = true;
        script.text = `var _iub = _iub || [];
            _iub.csConfiguration = ${JSON.stringify({
                consentOnContinuedBrowsing: false,
                countryDetection: process.env.STAGE === 'prod',
                floatingPreferencesButtonDisplay: 'bottom-right',
                gdprAppliesGlobally: false,
                lang: this.language.toLowerCase(),
                siteId: this.siteId,
                whitelabel: true,
                cookiePolicyId: !!this.defaultCookiesPolicy
                    ? this.defaultCookiesPolicy
                    : this.cookiesPolicy[this.language.toLowerCase()],
                reloadOnConsent: true,
                ...this.gdprConfig,
                banner: {
                    ...this.gdprConfig.banner,
                    position: 'float-bottom-left',
                    backgroundColor: '#ffffff',
                    acceptButtonDisplay: true,
                    rejectButtonDisplay: true,
                    explicitWithdrawal: true,
                    acceptButtonCaption: 'Accept all',
                    rejectButtonCaption: 'Decline',
                    acceptButtonColor: '#000000',
                    acceptButtonCaptionColor: '#ffffff',
                    rejectButtonColor: '#ffffff',
                    rejectButtonCaptionColor: '#000000',
                    textColor: '#000000',
                    content: `
                        <style>
                            #iubenda-cs-banner .iub-banner-content {
                                border-radius: 16px !important;
                                box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.12) !important;
                                max-width: 420px !important;
                                padding: 24px !important;
                            }
                            #iubenda-cs-banner .iubenda-cs-rationale {
                                max-width: 420px !important;
                            }
                            #iubenda-cs-banner .iubenda-banner-content-padded {
                                font-family: "Magnetik", "Inter", sans-serif;
                            }
                            #iubenda-cs-banner #iubenda-cs-title {
                                font-weight: 700 !important;
                                font-size: 16px !important;
                                color: #000000 !important;
                                background-color: transparent !important;
                            }
                            #iubenda-cs-banner #iubenda-cs-paragraph {
                                font-size: 13px !important;
                                color: #6b7280 !important;
                                line-height: 1.5 !important;
                            }
                            #iubenda-cs-banner .iubenda-cs-opt-group button {
                                border-radius: 9999px !important;
                                width: 179px !important;
                                height: 40px !important;
                                font-size: 14px !important;
                                font-weight: 500 !important;
                            }
                            #iubenda-cs-banner .iubenda-cs-reject-btn {
                                border: 1px solid #e5e7eb !important;
                                border-radius: 9999px !important;
                            }
                        </style>
                        <div id="iubenda-cs-title" style="background-color: transparent !important;">We value your privacy</div>
                        <div id="iubenda-cs-paragraph" style="margin-bottom: 10px; text-align: left; font-size: 13px; font-weight: 400; color: #6b7280;">
                            We use cookies to improve your experience and recommend the best workspaces for you.
                        </div>
                    `
                }
            })};`;
        document.head.appendChild(script);
    }

    addLib() {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.id = 'cd_lib';
        script.async = true;
        script.src = 'https://cdn.iubenda.com/cs/iubenda_cs.js';
        document.head.appendChild(script);
    }

    addInstructionLib() {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.id = 'cd_lib_instruction';
        script.async = true;
        script.src = this.consentLink;
        document.head.appendChild(script);
    }

    getConfigData(url: string): Promise<GDPRConfigType> {
        return fetch(url) as unknown as Promise<GDPRConfigType>;
    }

    isConcentGiven(): boolean {
        const concentCookies: ConcentType = JSON.parse(
            getCookie('_iub_cs-s' + this.siteId) ||
                getCookie(
                    '_iub_cs-' +
                        (!!this.cookiesPolicy
                            ? this.cookiesPolicy[this.language.toLowerCase()]
                            : '')
                ) ||
                '{}'
        ) as ConcentType;

        return concentCookies.consent;
    }
}
